import type { SubscriptionBlockId, ValidationOptions } from '@subscriptions/domain/types';
import type {
  PaymentDate,
  SubscriptionInterval,
  SubscriptionStatus,
  SubscriptionBlockSubStatus,
  CustomerState,
  StorePlan,
  PlanConceptParams,
  LastPaymentError,
  BlockDiscounts,
} from '@subscriptions/domain/value-objects';
import {
  createPaymentDate,
  createSubscriptionInterval,
  createSubscriptionStatus,
  createSubscriptionBlockSubStatus,
  createCustomerState,
  createStorePlan,
  createLastPaymentError,
  createBlockDiscounts,
} from '@subscriptions/domain/value-objects';
import { CustomerStates } from '@subscriptions/domain/types';
import { createValueResolver } from '@subscriptions/domain/utils';
import type { PaymentMethod } from './PaymentMethod';
import type { InvoiceData } from './InvoiceData';
import { createPaymentMethod } from './PaymentMethod';
import { createInvoiceData } from './InvoiceData';

/**
 * Subscription block domain entity.
 *
 * Immutable by design — represents a group of store subscriptions.
 * The paymentMethod and invoiceData fields are snapshots (not live references).
 * Only created/reconstructed via factory function.
 */
export type SubscriptionBlock = Readonly<{
  uuid: SubscriptionBlockId;
  name: string;
  extensionPos: number;
  extensionConnect: number;
  total: number;
  paymentDate: PaymentDate;
  interval: SubscriptionInterval;
  status: SubscriptionStatus;
  subStatus: SubscriptionBlockSubStatus;
  customerState: CustomerState;
  /** Date the subscription entered churn. Null when the backend does not report it. */
  inactiveSince: string | null;
  advanceInvoice: boolean;
  groupInvoice: boolean;
  configEnabled: boolean;
  paymentMethod: PaymentMethod | null;
  invoiceData: InvoiceData | null;
  lastError: LastPaymentError | null;
  discounts: BlockDiscounts | null;
  stores: StorePlan[];
}>;

/**
 * Creates a validated SubscriptionBlock entity.
 *
 * Internally calls createPaymentMethod() and createInvoiceData() for snapshot fields
 * to ensure validation runs even for embedded data.
 *
 * @param params - Raw primitives from infrastructure layer
 * @param options - Validation options; with `softValidation` (read flows) invalid fields degrade
 *   to empty fallbacks so the UI renders the rest of the block instead of failing entirely
 * @returns Frozen SubscriptionBlock entity
 * @throws Error if any required field is invalid (strict mode only)
 */
export const createSubscriptionBlock = (
  params: {
    uuid: string;
    name: string;
    extensionPos: number;
    extensionConnect: number;
    total: number;
    paymentDate: string;
    interval: string;
    status: string;
    subStatus: string;
    customerState?: string;
    inactiveSince?: string | null;
    advanceInvoice: boolean;
    groupInvoice: boolean;
    configEnabled: boolean;
    paymentMethod: {
      uuid: string;
      provider: string;
      providerId: string | null;
      providerPublicId: string;
      methodType: string;
      orgUuid: string;
      brand: string | null;
      expirationDate: string | null;
      lastFourDigits: string | null;
      status: string;
    } | null;
    invoiceData: {
      uuid: string;
      orgUuid: string;
      businessName: string;
      firstName: string;
      firstLastName: string;
      secondLastName: string;
      postalCode: string;
      rfc: string;
      cfdi: string;
      email: string;
      taxRegime: string;
    } | null;
    lastError: { code: string; concept: string; date: string } | null;
    discounts?: {
      total?: number | null;
      expirationDate?: string | null;
      stores?: { storeUuid: string; amount?: number | null; expirationDate?: string | null }[] | null;
    } | null;
    stores: {
      storeUuid: string;
      storeName: string;
      plan: string;
      churnedAt?: string | null;
      concepts: PlanConceptParams[];
    }[];
  },
  options?: ValidationOptions,
): SubscriptionBlock => {
  const { paymentMethod, invoiceData, lastError, discounts } = params;
  const resolve = createValueResolver(options);

  if (!options?.softValidation) {
    if (!params.uuid?.trim()) {
      throw new Error('Subscription block UUID is required');
    }
    if (!params.name?.trim()) {
      throw new Error('Subscription block name is required');
    }
  }

  return Object.freeze({
    uuid: params.uuid as SubscriptionBlockId,
    name: params.name,
    extensionPos: params.extensionPos,
    extensionConnect: params.extensionConnect,
    total: params.total,
    paymentDate: resolve<PaymentDate>(() => createPaymentDate(params.paymentDate), ''),
    interval: resolve<SubscriptionInterval>(() => createSubscriptionInterval(params.interval), ''),
    status: resolve<SubscriptionStatus>(() => createSubscriptionStatus(params.status), ''),
    subStatus: resolve<SubscriptionBlockSubStatus>(() => createSubscriptionBlockSubStatus(params.subStatus), ''),
    customerState: resolve<CustomerState>(() => createCustomerState(params.customerState ?? CustomerStates.ACTIVE), ''),
    inactiveSince: params.inactiveSince ?? null,
    advanceInvoice: params.advanceInvoice,
    groupInvoice: params.groupInvoice,
    configEnabled: params.configEnabled,
    paymentMethod: paymentMethod ? createPaymentMethod(paymentMethod, options) : null,
    invoiceData: invoiceData ? createInvoiceData(invoiceData, options) : null,
    lastError: lastError ? createLastPaymentError(lastError) : null,
    discounts: discounts ? resolve<BlockDiscounts | null>(() => createBlockDiscounts(discounts), null) : null,
    stores: params.stores.map((store) => createStorePlan(store, options)),
  });
};
